import {useAuthorize, useAuthIsProxy} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import StyledTeamsSettingsCardHeader
  from 'components/molecules/Cards/TeamsSettingsCardHeader/TeamsSettingsCardHeader.styles';
import Box from 'components/atoms/Layout/Box/Box';
import Typography from 'components/atoms/Text/Typography/Typography';
import Chip from 'components/atoms/Chips/Chip/Chip';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import ActionButton from 'components/molecules/Buttons/ActionButton/ActionButton';
import FieldInline from 'components/molecules/Inlines/FieldInline/FieldInline';
import constants from 'helpers/constants';
import useMediaQuery from '@mui/material/useMediaQuery';
import utils from 'helpers/utils';

const TeamsSettingsCardHeader = React.forwardRef((props, ref) => {
  const {
    team,
    canUpdate,
    addUsersButtonAction,
    deleteTeamButtonAction,
    onChange,
    ...innerProps
  } = useComponentProps(props, 'TeamsSettingsCardHeader');

  const authorize = useAuthorize();
  const isProxy = useAuthIsProxy();

  const smDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const nameFields = useMemo(() => {
    const fields = [];
    fields.push({
      name: 'name',
      placeholder: 'Type a team name',
      formGroup: 'main',
      type: constants.formFieldTypes.text,
      validation: constants.formFieldValidationTypes.text,
      initial: team?.name,
      required: true,
      FormFieldProps: {
        showTooltip: true,
        hiddenLabel: true,
        variant: 'outlined',
        size: 'small',
        InputLabelProps: {
          shrink: true,
        }
      }
    });

    fields.push({
      name: 'isPrivate',
      label: 'Private',
      formGroup: 'props',
      type: constants.formFieldTypes.checkbox,
      conversion: constants.formFieldConversionTypes.boolean,
      initial: team?.props?.isPrivate,
      disabled: !authorize({attribute: 'team.field.isPrivate.update', meta: {team}}),
      FormFieldProps: {
        variant: 'inlineLabel',
      }
    });

    fields.push({
      name: 'isExternal',
      label: 'External',
      formGroup: 'props',
      type: constants.formFieldTypes.checkbox,
      conversion: constants.formFieldConversionTypes.boolean,
      initial: team?.props?.isExternal,
      FormFieldProps: {
        variant: 'inlineLabel',
      }
    });

    return fields;
  }, [team, authorize]);

  const isActiveFields = useMemo(() => [{
    name: 'isActive',
    label: 'Status',
    type: constants.formFieldTypes.list,
    conversion: constants.formFieldConversionTypes.value,
    required: true,
    initial: team?.props?.isActive,
    options: 'toggleActiveInactive',
    FormFieldProps: {
      ListProps: {
        catchFocus: false
      }
    }
  }], [team?.props?.isActive]);

  const renderFormContent = (groups) => {
    const main = groups.find((g) => g.name === 'main');
    const props = groups.find((g) => g.name === 'props');

    return <Box style={{display: 'flex', flexDirection: 'column', rowGap: innerProps.theme.layout('1sp')}}>
      <Box className="Form-fields">
        {main.rendered}
      </Box>
      <Box style={{display: 'flex', columnGap: innerProps.theme.layout('2sp')}}>
        {props.rendered}
      </Box>
    </Box>
  }

  const handleChange = (field, value, onSuccess, onError) => {
    onChange?.(field, value, onSuccess, onError)
  };

  return <StyledTeamsSettingsCardHeader ref={ref} {...innerProps}>
    <Box className="TeamsSettingsCardHeader-info">
      <Box className="TeamsSettingsCardHeader-info-title">
        <FieldInline fields={nameFields}
                     canUpdate={canUpdate}
                     onChange={handleChange}
                     InlineFormProps={{
                       renderContent: renderFormContent
                     }}>
          <Box className="TeamsSettingsCardHeader-info-subtitle">
            <Typography variant="subtitle2"
                        color="text.secondary">{`${utils.formatNumber(team?.members?.filter((tm) => isProxy || tm.profile.type === constants.user.types.regular)?.length ?? 0)} user${(team?.members?.filter((tm) => isProxy || tm.profile.type === constants.user.types.regular)?.length ?? 0) === 1 ? '' : 's'}`}</Typography>
            {team?.props?.isPrivate ? <Chip size="small" label="Private" /> : null}
            {team?.props?.isExternal ? <Chip size="small" label="External" /> : null}
          </Box>
        </FieldInline>
      </Box>
    </Box>
    {!smDown ? <Box className="TeamsSettingsCardHeader-info-active">
      <FieldInline fields={isActiveFields}
                   canUpdate={canUpdate && authorize({attribute: 'team.field.isActive.update', meta: {team}})}
                   onChange={handleChange}/>
    </Box> : null}
    <Box className="TeamsSettingsCardHeader-buttons">
      {addUsersButtonAction?.label ? <ActionButton action={addUsersButtonAction} /> : <ActionIconButton action={addUsersButtonAction} />}
      <ActionIconButton action={deleteTeamButtonAction} />
    </Box>
  </StyledTeamsSettingsCardHeader>
});

TeamsSettingsCardHeader.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  team: PropTypes.object,
  canUpdate: PropTypes.bool,
  addUsersButtonAction: PropTypes.object,
  deleteTeamButtonAction: PropTypes.object,
  onChange: PropTypes.func
};

export default TeamsSettingsCardHeader;
